import { useEffect } from "react";
import { routeMeta } from "./routeMeta";
import { cardForRoute, cardTags } from "./ogCards";

export const SITE_URL = "https://d2index.com";
const SITE_NAME = "d2index";

// Sets the per-page head tags.
//
// Note on reach: social crawlers (Twitter, Slack, iMessage, Facebook) do not
// run JavaScript, so they never see any of this - they read the static tags in
// index.html and nothing else. What this is for is the browser tab title, the
// canonical URL, and Google, which does render JS. Per-page link previews would
// need prerendering on top of this.
//
// Written directly rather than with react-helmet: that package is unmaintained
// and produced no tags at all here under React 18.

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.rel ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

// Trailing slashes here are not cosmetic, and they are not uniform.
//
// The prerendered routes are written as <route>/index.html, so Netlify serves
// them at /charts/ and 301s /charts to it - their canonical needs the slash or
// it names a URL that redirects.
//
// Wallet pages are the opposite. They are not files; the SPA fallback answers
// them, so they resolve exactly as linked. Giving them a slash would be worse
// than untidy: it was a redirect on these URLs that lowercased the path and
// broke base58 addresses, which are case sensitive.
const PRERENDERED = new Set(
  Object.keys(routeMeta).filter((route) => route !== "/")
);

export const canonicalUrl = (path) => {
  if (path === "/") return `${SITE_URL}/`;
  const clean = path.replace(/\/$/, "");
  return PRERENDERED.has(clean) ? `${SITE_URL}${clean}/` : `${SITE_URL}${clean}`;
};

// Keyword first, brand last. "d2index" has no search volume, so leading with it
// spent the most valuable part of the title on a term nobody looks for.
const Seo = ({ title, description, path, noindex = false }) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const url = canonicalUrl(path);
  // The same card the served HTML carries for this route, so the tags do not
  // change underneath a renderer once the bundle runs. This used to default to
  // the home page screenshot, which put it on every page after hydration - with
  // the generic card's alt text still describing a different picture.
  const { image, imageAlt } = cardTags(SITE_URL, cardForRoute(path));

  useEffect(() => {
    document.title = fullTitle;

    setMeta('meta[name="description"]', { name: "description", content: description });
    // Set either way, never left behind: these tags are mutated in place as the
    // visitor moves between pages, so a noindex from a not-found page would
    // otherwise follow them onto a real one.
    setMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, follow" : "index, follow",
    });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: url });

    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    setMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:image"]', { property: "og:image", content: image });
    setMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });

    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
  }, [fullTitle, description, url, image, imageAlt, noindex]);

  return null;
};

export default Seo;
